import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useHistory, useLocation } from "react-router-dom";

import { FirebaseAuth } from "../../../utils/firebase";
import { APP_V2_APP_EVENTS, logError, logEvent } from "../../lib/analytics";
import { showErrorToast } from "../../lib/Notifications";
import { completeMagicLinkSignIn, mapCognitoError } from "../cognito/cognitoAuth";
import { AuthRedirectView } from "./AuthRedirectView";

const DEFAULT_REDIRECT_PATH = "/facility/dashboard";

/**
 * Validates a redirect URL is a safe relative path (prevents open redirect and XSS).
 */
function isSafeRedirectUrl(url: string): boolean {
  try {
    const trimmed = url.trim();
    const parsed = new URL(trimmed, globalThis.location.origin);

    if (parsed.origin !== globalThis.location.origin) {
      return false;
    }

    // Must start with '/' but not '//' (protocol-relative)
    if (!/^\/(?!\/)/.test(trimmed)) {
      return false;
    }

    // Disallow protocol-like patterns (e.g. 'javascript:')
    if (/^[^/?#]*:/.test(trimmed)) {
      return false;
    }

    // Disallow backslashes (can confuse some browsers)
    return !trimmed.includes("\\");
  } catch {
    return false;
  }
}

async function exchangeCognitoTokenForFirebase(cognitoIdToken: string): Promise<string> {
  const response = await fetch(
    `${(globalThis as unknown as Record<string, string>).api}/auth/exchange-cognito-token`,
    {
      method: "GET",
      headers: {
        Authorization: `Bearer ${cognitoIdToken}`,
      },
    }
  );

  if (!response.ok) {
    throw new Error(`Token exchange failed: ${response.status}`);
  }

  const data = (await response.json()) as { firebaseCustomToken?: string };
  if (!data.firebaseCustomToken) {
    throw new Error("Token exchange missing firebase custom token");
  }
  return data.firebaseCustomToken;
}

type AuthState = "confirm" | "signing-in" | "redirecting" | "error" | "invalid";

export function AuthRedirect() {
  const location = useLocation();
  const history = useHistory();
  const [authState, setAuthState] = useState<AuthState>("confirm");
  const [error, setError] = useState<string | undefined>();
  const hasAttemptedAuth = useRef(false);

  const searchParams = useMemo(() => new URLSearchParams(location.search), [location.search]);
  const payload = searchParams.get("payload");
  const email = searchParams.get("email");

  // Remove sensitive token from the URL immediately on mount
  useEffect(() => {
    globalThis.history.replaceState(null, "", globalThis.location.pathname);
  }, []);

  // Validate required params on mount
  useEffect(() => {
    if (!payload || !email) {
      setAuthState("invalid");
    }
  }, [payload, email]);

  const handleContinue = useCallback(async () => {
    if (hasAttemptedAuth.current) {
      return;
    }
    hasAttemptedAuth.current = true;

    setAuthState("signing-in");

    try {
      const requestedUrl = searchParams.get("requestedUrl");

      logEvent(APP_V2_APP_EVENTS.COGNITO_AUTH_REDIRECT_STARTED);

      if (!payload || !email) {
        const detail = !payload ? "payload" : "email";
        logError(APP_V2_APP_EVENTS.COGNITO_AUTH_REDIRECT_FAILURE, {
          error: new Error(`Missing ${detail} in magic link`),
          metadata: { errorCode: "auth/invalid-link", errorMessage: `Missing ${detail}` },
        });

        setError("This login link is invalid or incomplete. Please request a new login link.");
        setAuthState("error");
        return;
      }

      const cognitoCredentials = await completeMagicLinkSignIn({ payload, email });

      logEvent(APP_V2_APP_EVENTS.COGNITO_AUTH_REDIRECT_COGNITO_SUCCESS);

      const firebaseCustomToken = await exchangeCognitoTokenForFirebase(cognitoCredentials.idToken);

      await FirebaseAuth.signInWithCustomToken(firebaseCustomToken);

      logEvent(APP_V2_APP_EVENTS.COGNITO_AUTH_REDIRECT_SUCCESS);

      setAuthState("redirecting");

      // Store email so the login page can pre-fill it if it briefly flashes
      // during the session loading transition (matches Firebase behavior).
      if (email) {
        localStorage.setItem("emailForSignIn", email);
      }

      const target =
        requestedUrl && isSafeRedirectUrl(requestedUrl) ? requestedUrl : DEFAULT_REDIRECT_PATH;

      // Navigate immediately after Firebase sign-in. The auth state listener
      // (onIdTokenChanged) will populate the Redux session asynchronously;
      // waiting for session.userId here is unreliable because the listener
      // chain (getFacilityUser → fetchSession) may not complete in time.
      history.replace(target);
    } catch (authError: unknown) {
      const email = searchParams.get("email") ?? undefined;
      const mappedError = mapCognitoError(authError);
      // Show a link-expired message for generic/unknown errors (e.g. expired or invalid link).
      // For auth/user-not-found, show the actual "user not found" message so the user knows
      // there is no account for their email address.
      const errorMessage =
        mappedError.code === "auth/unknown"
          ? "Link verification failed. The link may have expired. Please request a new login link."
          : mappedError.message;

      logError(APP_V2_APP_EVENTS.COGNITO_AUTH_REDIRECT_FAILURE, {
        error: authError,
        metadata: { errorCode: mappedError.code, errorMessage: mappedError.message, email },
      });
      if (mappedError.code === "auth/user-type-not-allowed") {
        logEvent(APP_V2_APP_EVENTS.COGNITO_AUTH_USER_TYPE_NOT_ALLOWED, { email });
      }

      setError(errorMessage);
      setAuthState("error");
      showErrorToast(errorMessage);
    }
  }, [payload, email, searchParams, history]);

  return (
    <AuthRedirectView
      authState={authState}
      error={error}
      onContinue={() => {
        void handleContinue();
      }}
    />
  );
}
